import { parseUrlToIndexUiState } from '../search-router/url-handling';

/**
 * Removes `returnTo` from a saved-search URL and returns a stable string for storage and deduping.
 * Accepts absolute or relative URLs; invalid / missing input yields `''`.
 */
export function normalizeSavedSearchUrlInput(input: string): string {
  if (input == null || typeof input !== 'string') return '';
  const trimmed = input.trim();
  if (!trimmed) return '';
  try {
    const base =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://www.jobcrawls.com';
    const u = new URL(trimmed, base);
    u.searchParams.delete('returnTo');
    if (u.searchParams.toString() === '') {
      u.search = '';
    }
    return `${u.origin}${u.pathname}${u.search}`;
  } catch {
    return trimmed.startsWith('/') ? trimmed.split('#')[0] : `/${trimmed}`.split('#')[0];
  }
}

function pathAndSearchFromUrl(input: string): string {
  if (input == null || typeof input !== 'string') return '/';
  try {
    const base =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://www.jobcrawls.com';
    const u = new URL(input, base);
    return `${u.pathname}${u.search}`;
  } catch {
    const raw = String(input);
    return raw.startsWith('/') ? raw.split('#')[0] : `/${raw}`.split('#')[0];
  }
}

/**
 * True when the URL represents the default empty SRP (home / jobs index with no meaningful filters).
 * Used to avoid cluttering recent searches with `/en`, `/en/jobs`, etc.
 */
export function shouldSkipPersistingSavedSearchUrl(input: string): boolean {
  if (input == null || typeof input !== 'string' || !input.trim()) return true;
  const normalized = normalizeSavedSearchUrlInput(input);
  const pathAndSearch = pathAndSearchFromUrl(normalized);
  const state = parseUrlToIndexUiState(pathAndSearch);

  const q = state.query?.trim() ?? '';
  const hasQuery = q.length > 0;

  const refinements = state.refinementList ?? {};
  const hasRefinements = Object.keys(refinements).some((key) => {
    const v = refinements[key];
    if (!Array.isArray(v) || v.length === 0) return false;
    return v.some((x) => x != null && String(x).trim() !== '');
  });

  const sort = state.sort;
  const hasNonDefaultSort = sort != null && sort !== '' && sort !== 'relevance';

  const page = state.page;
  const hasSecondaryPage = page != null && !Number.isNaN(Number(page)) && Number(page) >= 1;

  const globalScope = state.isFinland === false;

  return !hasQuery && !hasRefinements && !hasNonDefaultSort && !hasSecondaryPage && !globalScope;
}
